import Typography from "@mui/material/Typography";
import MessageMarkdownContent from "_shared/_views/markdown/message-markdown-content";
import type { NotificationType } from "notifications/notification-type";
import TopicCategory from "notifications/topics/topic-category";
import { parseNotificationActionLink } from "notifications/notification-action-link";
import { isConversationRecord } from "messaging/conversations/conversation-links";

/**
 * The conversation a message notification is from — "Acme Litigation · Client Team", or the
 * proposal field a comment was left on — or nothing for any other notification (#5825).
 *
 * The API writes it into the notification's subheader when it records the message, and rewrites it
 * when a later message is folded into the same row, so it always names the newest message's
 * conversation. A message notification from before that, or from a direct chat, has none.
 */
export function getMessageNotificationConversation(notification: NotificationType): string | undefined {
  if (notification.topic?.category !== TopicCategory.Messaging.Forum) return undefined;

  return notification.subheader || undefined;
}

/**
 * Whether the row is about a proposal or a work agreement: one of its events, or a comment or message
 * in one of its conversations (#6880).
 *
 * The API rolls each reader's informational news about one record into one unread row, so such a row
 * can stand for events and comments from several people. A row that asks for an action is never
 * rolled, and so never counts more than one.
 */
export function isAboutARecord(notification: NotificationType): boolean {
  const category = notification.topic?.category;
  if (category === TopicCategory.Work.Proposal || category === TopicCategory.Work.Agreement) return true;
  if (category !== TopicCategory.Messaging.Forum) return false;

  const parent = parseNotificationActionLink(notification.actionLink)?.parent;
  return !!parent && isConversationRecord(parent.topicCategory);
}

/**
 * The record a row stands for once it has rolled more than one update into it (#6880): "Work.Proposal"
 * or "Work.Agreement". Nothing for a row that stands for one, or for any other notification.
 *
 * A rolled row is the record's notification, whatever its newest update was. Its title and icon are
 * the record's, so a row whose newest update is a comment does not read as one more message from
 * that comment's author.
 */
export function getRolledRecordCategory(notification: NotificationType): string | undefined {
  if ((notification.occurrenceCount ?? 1) <= 1) return undefined;

  const category = notification.topic?.category;
  if (category === TopicCategory.Work.Proposal || category === TopicCategory.Work.Agreement) return category;
  if (category !== TopicCategory.Messaging.Forum) return undefined;

  const parent = parseNotificationActionLink(notification.actionLink)?.parent;
  return parent && isConversationRecord(parent.topicCategory) ? parent.topicCategory : undefined;
}

/**
 * "and 2 more" after the newest message of a row that stands for three, or nothing for a row that
 * stands for one (#5825). A row about a proposal or work agreement says "and 2 more updates", because
 * what it counts can be events as well as comments (#6880).
 *
 * The row keeps only the newest text (#830), so the earlier ones are counted rather than shown.
 * Opening the conversation or the record is how a reader sees them.
 */
export function getMoreMessagesLabel(notification: NotificationType): string | undefined {
  const earlier = (notification.occurrenceCount ?? 1) - 1;
  if (earlier <= 0) return undefined;
  if (isAboutARecord(notification)) return `and ${earlier} more ${earlier === 1 ? "update" : "updates"}`;
  return notification.topic?.category === TopicCategory.Messaging.Forum ? `and ${earlier} more` : undefined;
}

export default function NotificationContent({
  notification,
  hideConversation = false,
}: Readonly<{
  notification: NotificationType;
  /** For a place that already names the conversation elsewhere on the row, as the Type column does. */
  hideConversation?: boolean;
}>) {
  if (notification.topic?.category === TopicCategory.Messaging.Forum) {
    return <MessageNotificationContent notification={notification} hideConversation={hideConversation} />;
  }

  const moreUpdates = getMoreMessagesLabel(notification);
  const hasStructuredContent = Boolean(notification.subheader || notification.body || notification.finePrint);
  if (!hasStructuredContent) {
    if (!notification.message?.value) return null;

    return (
      <>
        <MessageMarkdownContent compact content={notification.message.value} />
        <MoreUpdates label={moreUpdates} />
      </>
    );
  }

  return (
    <>
      {notification.subheader && <Typography variant="body2">{notification.subheader}</Typography>}
      {notification.body && <MessageMarkdownContent compact content={notification.body} />}
      {notification.finePrint && (
        <Typography component="div" variant="caption" color="text.disabled">
          {notification.finePrint}
        </Typography>
      )}
      <MoreUpdates label={moreUpdates} />
    </>
  );
}

/** How many earlier updates a rolled row stands for, set apart so it never reads as somebody's words. */
function MoreUpdates({ label }: Readonly<{ label: string | undefined }>) {
  if (!label) return null;

  return (
    <Typography component="span" variant="inherit" sx={{ color: "text.disabled", fontStyle: "italic" }}>
      {label}
    </Typography>
  );
}

/**
 * A message notification: which conversation it is from, on its own line, then the newest message
 * and how many came before it (#5825).
 *
 * Its subheader is the conversation rather than a summary, so it does not take the structured
 * branch, which would show the subheader in place of the message.
 */
function MessageNotificationContent({
  notification,
  hideConversation,
}: Readonly<{ notification: NotificationType; hideConversation: boolean }>) {
  const conversation = hideConversation ? undefined : getMessageNotificationConversation(notification);
  const moreMessages = getMoreMessagesLabel(notification);
  // A rolled row is titled with its record, not its newest author, so the author is named here.
  const author = getRolledRecordCategory(notification) ? notification.messageInfo?.senderName?.value : undefined;

  return (
    <>
      {conversation && (
        <Typography component="span" variant="caption" noWrap sx={{ display: "block", fontWeight: 500 }}>
          {conversation}
        </Typography>
      )}
      {author && notification.message?.value && (
        <Typography component="span" variant="inherit" sx={{ fontWeight: 500 }}>
          {author}:{" "}
        </Typography>
      )}
      {notification.message?.value && <MessageMarkdownContent compact content={notification.message.value} />}
      {/* Set apart from the message it follows, so "and 2 more" never reads as words somebody wrote. */}
      {moreMessages && notification.message?.value && " "}
      <MoreUpdates label={moreMessages} />
    </>
  );
}
